import React, { useEffect, useMemo, useState } from "react";
import {
  FaUserClock,
  FaSyncAlt,
  FaSearch,
  FaEdit,
  FaTimes,
  FaSave,
  FaCalculator,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { getData, putData } from "../../../services/FetchNodeservices";

export default function EmpLeaveBalance() {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // ✅ Edit Modal State
  const [openEdit, setOpenEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const [form, setForm] = useState({
    carry_forward: 0,
    cl_sl_remaining: 0,
    pl_annual_remaining: 0,
    total_leaves: 0,
    short_leave: 0,
    half_day: 0,
    full_day: 0,
  });

  /* ================= FETCH ================= */
  const fetchLeaveBalance = async () => {
    try {
      setLoading(true);
      const res = await getData("employee-leaves/all");
      if (res?.status) {
        setLeaves(res.data || []);
      } else {
        toast.error(res?.message || "Failed to fetch leave balance");
      }
    } catch (err) {
      console.error(err);
      toast.error("Server error while fetching leave balance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveBalance();
  }, []);

  /* ================= FILTER ================= */
  const filteredLeaves = useMemo(() => {
    if (!search) return leaves;
    return leaves.filter((x) =>
      (x.full_name || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [leaves, search]);

  /* ================= HELPERS ================= */
  const formatDate = (d) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleDateString("en-GB");
    } catch {
      return d;
    }
  };

  const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  const intNum = (v) => {
    const n = parseInt(v);
    return isNaN(n) ? 0 : n;
  };

  const openEditModal = (row) => {
    setSelected(row);
    setOpenEdit(true);

    setForm({
      carry_forward: num(row.carry_forward),
      cl_sl_remaining: num(row.cl_sl_remaining),
      pl_annual_remaining: num(row.pl_annual_remaining),
      total_leaves: num(row.total_leaves),
      short_leave: intNum(row.short_leave),
      half_day: intNum(row.half_day),
      full_day: intNum(row.full_day),
    });
  };

  const closeModal = () => {
    setOpenEdit(false);
    setSelected(null);
  };

  const handleChange = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  /* ================= AUTO TOTAL ================= */
  const autoTotal = () => {
    const total =
      num(form.carry_forward) +
      num(form.cl_sl_remaining) +
      num(form.pl_annual_remaining);

    setForm((prev) => ({ ...prev, total_leaves: total.toFixed(2) }));
  };

  /* ================= UPDATE ================= */
  const handleUpdate = async () => {
    if (!selected?.id) return;

    try {
      setSaving(true);

      const payload = {
        carry_forward: num(form.carry_forward),
        cl_sl_remaining: num(form.cl_sl_remaining),
        pl_annual_remaining: num(form.pl_annual_remaining),
        total_leaves: num(form.total_leaves),
        short_leave: intNum(form.short_leave),
        half_day: intNum(form.half_day),
        full_day: intNum(form.full_day),
      };

      // ✅ BACKEND ROUTE: employee-leaves/update/:id
      const res = await putData(
        `employee-leaves/update/${selected.id}`,
        payload
      );

      if (res?.status) {
        toast.success(res.message || "Leave updated ✅");
        closeModal();
        fetchLeaveBalance(); // ✅ refresh data
      } else {
        toast.error(res?.message || "Update failed ❌");
      }
    } catch (err) {
      console.error(err);
      toast.error("Server error while updating leave");
    } finally {
      setSaving(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-gray-50 pt-20 px-4 md:ml-72 md:px-6">
      <div className="bg-white shadow-xl rounded-xl border border-gray-200 p-4 sm:p-6">
        {/* HEADER */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b pb-4 mb-4">
          <div className="flex items-center gap-2">
            <FaUserClock size={22} className="text-blue-600" />
            <h2 className="text-lg font-bold text-gray-700">
              Employee Leave Balance
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {/* SEARCH */}
            <div className="relative w-full sm:w-72">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search employee..."
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>

            {/* REFRESH */}
            <button
              onClick={fetchLeaveBalance}
              className="px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2 bg-blue-600 text-white hover:bg-blue-700"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-[1250px] w-full text-sm">
            <thead className="bg-gray-100 border-b text-gray-700 text-xs uppercase">
              <tr>
                <th className="p-3 text-left whitespace-nowrap">Employee</th>
                <th className="p-3 text-center whitespace-nowrap">Carry Fwd</th>
                <th className="p-3 text-center whitespace-nowrap">CL Rem</th>
                <th className="p-3 text-center whitespace-nowrap">PL Rem</th>
                <th className="p-3 text-center whitespace-nowrap">Total</th>
                <th className="p-3 text-center whitespace-nowrap">Short</th>
                <th className="p-3 text-center whitespace-nowrap">Half</th>
                <th className="p-3 text-center whitespace-nowrap">Full</th>
                <th className="p-3 text-center whitespace-nowrap">Updated</th>
                {/* <th className="p-3 text-center whitespace-nowrap">Action</th> */}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="p-6 text-center text-gray-500">
                    Loading leave balances...
                  </td>
                </tr>
              ) : filteredLeaves.length === 0 ? (
                <tr>
                  <td colSpan="10" className="p-6 text-center text-gray-500">
                    No employee leave data found.
                  </td>
                </tr>
              ) : (
                filteredLeaves.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <td className="p-3 whitespace-nowrap font-medium text-gray-700">
                      {row.full_name}
                    </td>

                    <td className="p-3 text-center">
                      {num(row.carry_forward).toFixed(2)}
                    </td>
                    <td className="p-3 text-center">
                      {num(row.cl_sl_remaining).toFixed(2)}
                    </td>
                    <td className="p-3 text-center">
                      {num(row.pl_annual_remaining).toFixed(2)}
                    </td>
                    <td className="p-3 text-center font-semibold text-gray-700">
                      {num(row.total_leaves).toFixed(2)}
                    </td>

                    <td className="p-3 text-center">{row.short_leave || 0}</td>
                    <td className="p-3 text-center">{row.half_day || 0}</td>
                    <td className="p-3 text-center">{row.full_day || 0}</td>

                    <td className="p-3 text-center text-gray-500 whitespace-nowrap">
                      {formatDate(row.updated_at || row.created_at)}
                    </td>

                    {/* <td className="p-3 text-center">
                      <button
                        onClick={() => openEditModal(row)}
                        className="px-3 py-1 text-xs rounded-lg flex items-center gap-2 justify-center bg-indigo-600 text-white hover:bg-indigo-700 mx-auto"
                      >
                        <FaEdit /> Edit
                      </button>
                    </td> */}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* FOOTER COUNT */}
        <div className="mt-4 text-sm text-gray-500">
          Showing <b>{filteredLeaves.length}</b> of <b>{leaves.length}</b> records
        </div>
      </div>

      {/* ✅ EDIT MODAL */}
      {openEdit && selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-xl border overflow-hidden">
            {/* header */}
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h3 className="font-bold text-gray-800 text-lg">
                  Edit Leave Balance
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Employee: <b>{selected.full_name}</b>
                </p>
              </div>

              <button
                onClick={closeModal}
                className="p-2 rounded hover:bg-gray-100 text-gray-700"
              >
                <FaTimes />
              </button>
            </div>

            {/* body */}
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                label="Carry Forward"
                value={form.carry_forward}
                onChange={(v) => handleChange("carry_forward", v)}
              />
              <Field
                label="CL Remaining"
                value={form.cl_sl_remaining}
                onChange={(v) => handleChange("cl_sl_remaining", v)}
              />
              <Field
                label="PL Remaining"
                value={form.pl_annual_remaining}
                onChange={(v) => handleChange("pl_annual_remaining", v)}
              />
              <Field
                label="Total Leaves"
                value={form.total_leaves}
                onChange={(v) => handleChange("total_leaves", v)}
              />

              <Field
                label="Short Taken"
                value={form.short_leave}
                onChange={(v) => handleChange("short_leave", v)}
                step="1"
              />
              <Field
                label="Half Taken"
                value={form.half_day}
                onChange={(v) => handleChange("half_day", v)}
                step="1"
              />
              <Field
                label="Full Taken"
                value={form.full_day}
                onChange={(v) => handleChange("full_day", v)}
                step="1"
              />
            </div>

            {/* footer */}
            <div className="px-5 py-4 border-t flex flex-col sm:flex-row gap-2 justify-end">
              <button
                onClick={autoTotal}
                className="px-4 py-2 rounded-lg border text-sm flex items-center gap-2 hover:bg-gray-50"
              >
                <FaCalculator /> Auto Total
              </button>

              <button
                onClick={closeModal}
                className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>

              <button
                onClick={handleUpdate}
                disabled={saving}
                className={`px-4 py-2 rounded-lg text-white flex items-center justify-center gap-2 text-sm ${
                  saving ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"
                }`}
              >
                <FaSave />
                {saving ? "Saving..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ✅ Input Field */
function Field({ label, value, onChange, step = "0.25" }) {
  return (
    <div className="flex flex-col">
      <label className="text-xs font-semibold text-gray-600 mb-1">{label}</label>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
      />
    </div>
  );
}
